import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "./api";
import type { HubNotification, NotificationAction, NotificationBucket } from "../types";
import { useSseEvent } from "./useSseEvent";

// The top-bar bell's data. Fetches the caller's OWN notifications (server-scoped
// by OIDC sub) on mount, and refetches on every `notification_ping` SSE event —
// producers across the app emit that contentless ping after persisting a
// notification, so it doubles as a "your notifications may have changed" signal
// WITHOUT putting any notification content on the wire (the list always comes back
// through the self-scoped REST endpoint). No polling timer needed.
//
// It now fetches ONE TAB at a time (ENG-56868). Nothing here is deleted
// automatically, so Read and Archived only grow; a single list of everything
// would be a page of the inbox dressed up as the inbox, and "select all in this
// tab" would select a page. The per-tab counts come from their own read for the
// same reason — counting the fetched rows would report the cap.

export interface NotificationsData {
  /** The rows in the ACTIVE tab, newest first. */
  items: HubNotification[];
  /** How many rows each tab holds — the bell's badge is `counts.unread`. */
  counts: Record<NotificationBucket, number>;
  unread: number;
  bucket: NotificationBucket;
  setBucket: (bucket: NotificationBucket) => void;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  /** Apply one action to any number of rows. Resolves to the ids it could NOT
   *  reach, so a partial failure can be named rather than counted. */
  apply: (ids: string[], action: NotificationAction) => Promise<string[]>;
  refresh: () => Promise<void>;
}

const EMPTY_COUNTS: Record<NotificationBucket, number> = { unread: 0, read: 0, archived: 0 };

export function useNotifications(): NotificationsData {
  const [items, setItems] = useState<HubNotification[]>([]);
  const [counts, setCounts] = useState<Record<NotificationBucket, number>>(EMPTY_COUNTS);
  const [bucket, setBucket] = useState<NotificationBucket>("unread");
  // The tab a response belongs to, so a slow fetch for a tab the reader has
  // already left cannot paint over the one they are looking at.
  const wanted = useRef<NotificationBucket>(bucket);
  wanted.current = bucket;

  const refresh = useCallback(async () => {
    const forTab = wanted.current;
    try {
      const [rows, tally] = await Promise.all([
        api.getNotifications(forTab),
        api.getNotificationCounts(),
      ]);
      if (wanted.current !== forTab) return;
      setItems(rows);
      setCounts(tally);
    } catch {
      // Best-effort surface; a failed poll shouldn't blank the bell.
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh, bucket]);

  // Producers emit notification_ping after writing, so refetch on it —
  // near-real-time delivery, leak-free.
  useSseEvent<Record<string, never>>("notification_ping", () => {
    void refresh();
  });

  const markRead = useCallback(async (id: string) => {
    // Optimistic: flip locally, then persist (a failed call is corrected on the
    // next refresh).
    setItems((prev) => prev.map((n) => (n.id === id && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n)));
    setCounts((prev) => ({ ...prev, unread: Math.max(0, prev.unread - 1), read: prev.read + 1 }));
    try {
      await api.markNotificationRead(id);
    } catch {
      void refresh();
    }
  }, [refresh]);

  const markAllRead = useCallback(async () => {
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: now })));
    try {
      await api.markAllNotificationsRead();
    } catch { /* corrected by the refresh below */ }
    await refresh();
  }, [refresh]);

  const apply = useCallback(async (ids: string[], action: NotificationAction): Promise<string[]> => {
    if (!ids.length) return [];
    // NOT optimistic: every one of these moves a row between tabs (or removes
    // it), so a guess that turns out wrong is a row that vanished from one tab
    // and never appeared in another. One round trip, then the truth.
    const res = await api.applyNotificationAction(ids, action).catch(() => null);
    if (!res || !res.ok) { await refresh(); return ids; }
    const body = await res.json().catch(() => null);
    if (body?.counts) setCounts(body.counts);
    await refresh();
    return Array.isArray(body?.missing) ? body.missing.map(String) : [];
  }, [refresh]);

  const unread = counts.unread;
  const value = useMemo(
    () => ({ items, counts, unread, bucket, setBucket, markRead, markAllRead, apply, refresh }),
    [items, counts, unread, bucket, markRead, markAllRead, apply, refresh],
  );
  return value;
}
